import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, BarChart3, CalendarRange, PieChart as PieChartIcon } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { getSystemLogs } from '../api/systemLogs';
import { toast } from 'sonner';

const LEVEL_OPTIONS = ['info', 'success', 'warn', 'error'];
const LEVEL_COLORS = {
  info: '#3b82f6',
  success: '#10b981',
  warn: '#f59e0b',
  error: '#ef4444',
};

const formatDay = (value) => {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleDateString();
};

const SystemLogReport = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedLevels, setSelectedLevels] = useState(['warn', 'error']);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await getSystemLogs({
        page: 1,
        limit: 500,
        level: selectedLevels.join(','),
        from: fromDate ? `${fromDate} 00:00:00` : '',
        to: toDate ? `${toDate} 23:59:59` : '',
      });
      setLogs(Array.isArray(response?.data?.logs) ? response.data.logs : []);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load log report');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleLevel = (level) => {
    setSelectedLevels((prev) => {
      if (prev.includes(level)) {
        if (prev.length === 1) return prev;
        return prev.filter((item) => item !== level);
      }
      return [...prev, level];
    });
  };

  const openLogsWithFilters = (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.level) params.set('level', filters.level);
    if (filters.action) params.set('action', filters.action);
    if (filters.q) params.set('q', filters.q);
    if (fromDate) params.set('from', fromDate);
    if (toDate) params.set('to', toDate);
    const qs = params.toString();
    const targetPath = qs ? `/super-admin/logs?${qs}` : '/super-admin/logs';
    navigate(targetPath);
  };

  const reportData = useMemo(() => {
    const levelCounts = { info: 0, success: 0, warn: 0, error: 0 };
    const timelineMap = {};
    const actionMap = {};

    logs.forEach((log) => {
      const level = String(log?.level || '').toLowerCase();
      if (levelCounts[level] !== undefined) {
        levelCounts[level] += 1;
      }

      const dayKey = formatDay(log?.created_at);
      timelineMap[dayKey] = timelineMap[dayKey] || { day: dayKey, info: 0, success: 0, warn: 0, error: 0, total: 0 };
      if (timelineMap[dayKey][level] !== undefined) {
        timelineMap[dayKey][level] += 1;
      }
      timelineMap[dayKey].total += 1;

      const action = String(log?.action || 'unknown').toLowerCase();
      actionMap[action] = (actionMap[action] || 0) + 1;
    });

    const byLevel = Object.entries(levelCounts).map(([name, value]) => ({
      name,
      value,
      fill: LEVEL_COLORS[name] || '#64748b',
    }));
    const timeline = Object.values(timelineMap).sort((a, b) => new Date(a.day) - new Date(b.day));
    const byAction = Object.entries(actionMap)
      .map(([action, count]) => ({ action, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    return {
      total: logs.length,
      levelCounts,
      byLevel,
      timeline,
      byAction,
    };
  }, [logs]);

  return (
    <DashboardLayout userRole="super_admin">
      <div className="w-full min-h-0 bg-slate-50 dark:bg-slate-900 pb-3">
        <div className="space-y-6">
          <div className="rounded-2xl tenant-brand-gradient text-white p-6 shadow-lg shadow-indigo-500/20">
            <h1 className="text-2xl font-bold tracking-tight">System Log Report</h1>
            <p className="text-white/80 text-sm mt-1">
              Analyze warning/error trends, login and ticket activity with multi-level filters.
            </p>
          </div>

          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-4">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label htmlFor="log-report-from" className="block text-xs font-semibold mb-1 text-slate-500">From</label>
                <input
                  id="log-report-from"
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900"
                />
              </div>
              <div>
                <label htmlFor="log-report-to" className="block text-xs font-semibold mb-1 text-slate-500">To</label>
                <input
                  id="log-report-to"
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900"
                />
              </div>
              <button
                type="button"
                onClick={fetchLogs}
                className="px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700"
              >
                Apply Filters
              </button>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              {LEVEL_OPTIONS.map((level) => {
                const active = selectedLevels.includes(level);
                return (
                  <button
                    key={level}
                    type="button"
                    onClick={() => toggleLevel(level)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                      active
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200'
                    }`}
                  >
                    {level.toUpperCase()}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
              <div className="text-xs text-slate-500 font-semibold">Total Logs</div>
              <div className="text-2xl font-bold mt-1">{reportData.total}</div>
            </div>
            {LEVEL_OPTIONS.map((level) => (
              <button
                key={level}
                type="button"
                onClick={() => openLogsWithFilters({ level })}
                className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 text-left hover:bg-slate-50 dark:hover:bg-slate-700/60 transition"
              >
                <div className="text-xs text-slate-500 font-semibold">{level.toUpperCase()}</div>
                <div className="text-2xl font-bold mt-1" style={{ color: LEVEL_COLORS[level] }}>
                  {reportData.levelCounts[level]}
                </div>
                <div className="text-[11px] text-slate-400 mt-1">Open in logs</div>
              </button>
            ))}
          </div>

          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">Quick filters to logs page</div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => openLogsWithFilters({ level: 'error' })} className="px-3 py-1.5 rounded-full text-xs font-semibold border border-red-200 text-red-700 bg-red-50 hover:bg-red-100">
                Errors only
              </button>
              <button type="button" onClick={() => openLogsWithFilters({ level: 'warn' })} className="px-3 py-1.5 rounded-full text-xs font-semibold border border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100">
                Warnings only
              </button>
              <button type="button" onClick={() => openLogsWithFilters({ level: 'error,warn' })} className="px-3 py-1.5 rounded-full text-xs font-semibold border border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100">
                Errors + Warnings
              </button>
              <button type="button" onClick={() => openLogsWithFilters({ action: 'auth.login.failed' })} className="px-3 py-1.5 rounded-full text-xs font-semibold border border-slate-200 text-slate-700 bg-slate-50 hover:bg-slate-100">
                Login failures
              </button>
              <button type="button" onClick={() => openLogsWithFilters({ action: 'ticket.status_changed' })} className="px-3 py-1.5 rounded-full text-xs font-semibold border border-slate-200 text-slate-700 bg-slate-50 hover:bg-slate-100">
                Ticket status changes
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
              <div className="flex items-center gap-2 mb-4 text-sm font-semibold">
                <CalendarRange className="w-4 h-4" />
                Timeline (Line Chart)
              </div>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={reportData.timeline}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="error" stroke={LEVEL_COLORS.error} strokeWidth={2} />
                    <Line type="monotone" dataKey="warn" stroke={LEVEL_COLORS.warn} strokeWidth={2} />
                    <Line type="monotone" dataKey="success" stroke={LEVEL_COLORS.success} strokeWidth={2} />
                    <Line type="monotone" dataKey="info" stroke={LEVEL_COLORS.info} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
              <div className="flex items-center gap-2 mb-4 text-sm font-semibold">
                <PieChartIcon className="w-4 h-4" />
                Log Levels (Pie Chart)
              </div>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={reportData.byLevel} dataKey="value" nameKey="name" outerRadius={120} label>
                      {/* colors are provided via per-entry `fill` */}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
            <div className="flex items-center gap-2 mb-4 text-sm font-semibold">
              <BarChart3 className="w-4 h-4" />
              Top Actions (Bar Chart)
            </div>
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={reportData.byAction}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="action" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#6366f1" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {loading && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 text-amber-700 p-3 text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Loading log report...
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SystemLogReport;

